import {catalogItem} from './catalog.js';
import {pileMaterialProfile} from './pile-material-profiles.js';
const clamp=(value,min,max)=>Math.max(min,Math.min(max,value));
const FIXED_STEP=1/60;
export const PILE_LIMIT=8;

function rotationMatrix(body){
  const {x=0,y=0,z=0}=body.rotation??{};
  if(body.supportAngles?.[0]===x&&body.supportAngles[1]===y&&body.supportAngles[2]===z)return body.supportMatrix;
  const a=Math.cos(x),b=Math.sin(x),c=Math.cos(y),d=Math.sin(y),e=Math.cos(z),f=Math.sin(z);
  body.supportAngles=[x,y,z];
  return body.supportMatrix=[c*e,-c*f,d,a*f+b*e*d,a*e-b*f*d,-b*c,b*f-a*e*d,b*e+a*f*d,a*c];
}

// Support includes the deformed cage center; its pose origin stays unchanged.
function centerOffset(body,center=body.shapeCenter??[0,0,0]){
  const m=rotationMatrix(body),scale=body.radius*2;
  return [(m[0]*center[0]+m[1]*center[1]+m[2]*center[2])*scale,(m[3]*center[0]+m[4]*center[1]+m[5]*center[2])*scale,(m[6]*center[0]+m[7]*center[1]+m[8]*center[2])*scale];
}
function extent(body,normal,shape=body.shape??[1,1,1]){
  if(shape[0]===shape[1]&&shape[1]===shape[2])return body.radius*shape[0]*Math.hypot(...normal);
  const m=rotationMatrix(body),[x,y,z]=normal;
  return body.radius*Math.hypot((m[0]*x+m[3]*y+m[6]*z)*shape[0],(m[1]*x+m[4]*y+m[7]*z)*shape[1],(m[2]*x+m[5]*y+m[8]*z)*shape[2]);
}
export function directionSupport(body,normal){
  const center=centerOffset(body);
  return extent(body,normal)+center.reduce((sum,n,a)=>sum+n*normal[a],0);
}
export const floorSupport=body=>directionSupport(body,[0,-1,0]);
const broadRadius=body=>body.radius*Math.max(...body.shape)+Math.hypot(...(body.shapeCenter??[0,0,0]))*body.radius*2;
export function supportPoint(body,normal,rest=false){
  const shape=rest?body.restShape:body.shape,offset=centerOffset(body,rest?[0,0,0]:body.shapeCenter),support=extent(body,normal,shape);
  if(support<1e-10)return [body.x+offset[0],body.y+offset[1],body.z+offset[2]];
  const m=rotationMatrix(body),[nx,ny,nz]=normal,r2=body.radius*body.radius;
  const x=(m[0]*nx+m[3]*ny+m[6]*nz)*shape[0]**2*r2/support;
  const y=(m[1]*nx+m[4]*ny+m[7]*nz)*shape[1]**2*r2/support;
  const z=(m[2]*nx+m[5]*ny+m[8]*nz)*shape[2]**2*r2/support;
  return [body.x+offset[0]+m[0]*x+m[1]*y+m[2]*z,body.y+offset[1]+m[3]*x+m[4]*y+m[5]*z,body.z+offset[2]+m[6]*x+m[7]*y+m[8]*z];
}

export function choosePileToys(collection,random=Math.random,limit=PILE_LIMIT){
  const pool=collection.slice(),count=Math.min(pool.length,PILE_LIMIT,Math.max(0,Math.floor(limit)));
  // Partial Fisher-Yates gives every acquired instance an equal chance.
  for(let i=0;i<count;i++){
    const j=i+Math.floor(random()*(pool.length-i));
    [pool[i],pool[j]]=[pool[j],pool[i]];
  }
  return pool.slice(0,count);
}

export class CollectionPile {
  constructor(toys,{random=Math.random,reducedMotion=false,halfWidth=2.2,halfDepth=1.4,radiusScale=1,pourOrigin=null,shapes=null}={}){
    this.halfWidth=halfWidth;this.halfDepth=halfDepth;
    this.pourOrigin=pourOrigin;this.elapsed=0;
    // A few shuffled tiers avoid initial interpenetration and very high drops.
    const spacing=.94*radiusScale,maxRadius=.45*radiusScale;
    const columns=Math.max(1,Math.floor(2*Math.min(1.2,halfWidth-maxRadius)/spacing)+1);
    const rows=Math.max(1,Math.floor(2*Math.min(1.2,halfDepth-maxRadius)/spacing)+1),slots=[];
    for(let z=0;z<rows;z++)for(let x=0;x<columns;x++)slots.push([(x-(columns-1)/2)*spacing,(z-(rows-1)/2)*spacing]);
    for(let i=slots.length-1;i>0;i--){const j=Math.floor(random()*(i+1));[slots[i],slots[j]]=[slots[j],slots[i]];}
    this.bodies=toys.map((toy,i)=>{
      const radius=(.32+random()*.13)*radiusScale,slot=slots[i%slots.length],tier=Math.floor(i/slots.length);
      const shape=shapes?.get?shapes.get(toy.id):shapes?.[toy.id];
      return {
        id:toy.id,kind:toy.kind,material:catalogItem(toy.kind)?.material??'mochi',radius,shape:shape?[...shape]:[1,1,1],restShape:shape?[...shape]:[1,1,1],shapeCenter:[0,0,0],
        x:slot[0]+(random()-.5)*.016,
        y:Math.min(4.98,1.1+tier*spacing+random()*.016),
        z:slot[1]+(random()-.5)*.016,
        rotation:{x:(random()-.5)*.7,y:random()*Math.PI*2,z:(random()-.5)*.7},
        vx:0,vy:0,vz:0,visible:!pourOrigin,delay:pourOrigin?.35+i*.15:0,contacts:[],weight:1,
      };
    });
    this.held=null;this.target=null;this.accumulator=0;this.quietTime=0;
    this.sleeping=this.bodies.length===0;this.active=!this.sleeping;
    // Prewarm the same contacts offscreen; reduced motion never shows the fall.
    if(reducedMotion)for(let frame=0;frame<1800&&this.active;frame++)this.advance(FIXED_STEP);
    this.updateContacts();
  }
  setShape(id,shape,center=[0,0,0]){
    const body=this.bodies.find(b=>b.id===id);
    if(!body||shape.length!==3||center.length!==3||!shape.every(n=>Number.isFinite(n)&&n>0)||!center.every(Number.isFinite))return;
    // Accumulate tiny changes against the last accepted proxy to allow sleep.
    if(Math.max(...shape.map((n,a)=>Math.abs(n-body.shape[a])),...center.map((n,a)=>Math.abs(n-body.shapeCenter[a])))<.001)return;
    body.shape=[...shape];body.shapeCenter=[...center];
    this.sleeping=false;this.active=true;this.quietTime=0;
  }
  drag(id,target){
    const body=this.bodies.find(b=>b.id===id);
    if(!body?.visible||![target?.x,target?.y,target?.z].every(Number.isFinite))return;
    this.held=body;this.target={
      x:clamp(target.x,-this.halfWidth+directionSupport(body,[-1,0,0]),this.halfWidth-directionSupport(body,[1,0,0])),
      y:clamp(target.y,floorSupport(body),7),
      z:clamp(target.z,-this.halfDepth+directionSupport(body,[0,0,-1]),this.halfDepth-directionSupport(body,[0,0,1])),
    };
    this.sleeping=false;this.active=true;this.quietTime=0;
  }
  release(){
    if(!this.held)return;
    this.held=null;this.target=null;this.sleeping=false;this.active=true;this.quietTime=0;
  }
  step(elapsed){
    if(this.sleeping||!Number.isFinite(elapsed)||elapsed<=0)return this.active;
    this.accumulator+=Math.min(elapsed,.1);
    while(this.accumulator>=FIXED_STEP){
      this.advance(FIXED_STEP);this.accumulator-=FIXED_STEP;
      if(this.sleeping){this.accumulator=0;break;}
    }
    return this.active;
  }
  advance(dt){
    this.elapsed+=dt;
    for(const b of this.bodies){
      if(!b.visible){
        if(this.elapsed+1e-8<b.delay)continue;
        const origin=typeof this.pourOrigin==='function'?this.pourOrigin():this.pourOrigin;
        [b.x,b.y,b.z]=Array.isArray(origin)?origin:[origin.x,origin.y,origin.z];
        b.vx=1.3;b.vy=.2;b.vz=1;b.visible=true;
      }
      b.previousX=b.x;b.previousY=b.y;b.previousZ=b.z;
      b.floorImpact=0;
      if(b===this.held){
        // Follow the finger without teleporting through the other toys.
        const dx=this.target.x-b.x,dy=this.target.y-b.y,dz=this.target.z-b.z;
        const distance=Math.hypot(dx,dy,dz),follow=Math.min(1-Math.exp(-8*dt),6*dt/(distance||1));
        b.x+=dx*follow;b.y+=dy*follow;b.z+=dz*follow;b.vx=b.vy=b.vz=0;
      }else{
        const profile=pileMaterialProfile(b.material),damping=Math.exp(-dt*(b.y<=floorSupport(b)+.015?profile.groundDrag:profile.airDrag));
        b.vx*=damping;b.vz*=damping;b.vy=Math.max(-8,b.vy-12*dt);
        b.x+=b.vx*dt;b.y+=b.vy*dt;b.z+=b.vz*dt;
      }
      b.incoming=[b.vx,b.vy,b.vz];
    }
    this.impactPairs=[];this.projectContacts(24);
    let motion=0;
    for(const b of this.bodies){
      if(!b.visible)continue;
      this.contain(b);
      let dx=b.x-b.previousX,dy=b.y-b.previousY,dz=b.z-b.previousZ;
      b.rotation.x+=dz/b.radius*.4;b.rotation.z-=dx/b.radius*.4;
      this.contain(b);dx=b.x-b.previousX;dy=b.y-b.previousY;dz=b.z-b.previousZ;
      motion=Math.max(motion,Math.hypot(dx,dy,dz));
      if(b!==this.held){
        // Carry only displacement that survived the contact constraints into
        // the next frame; canceled gravity must not accumulate inside a stack.
        const speed=Math.hypot(dx,dy,dz)/dt,scale=Math.min(1,8/(speed||1))/dt;
        b.vx=dx*scale;b.vy=dy*scale;b.vz=dz*scale;
        if(b.floorImpact>.65)b.vy=Math.max(b.vy,b.floorImpact*pileMaterialProfile(b.material).restitution);
      }
    }
    for(const {a,b,normal,closing} of this.impactPairs){
      const restitution=Math.min(pileMaterialProfile(a.material).restitution,pileMaterialProfile(b.material).restitution);
      if(!restitution)continue;
      const separating=(b.vx-a.vx)*normal[0]+(b.vy-a.vy)*normal[1]+(b.vz-a.vz)*normal[2];
      const wa=a===this.held?0:1,wb=b===this.held?0:1,impulse=Math.max(0,-closing*restitution-separating)/(wa+wb);
      a.vx-=normal[0]*impulse*wa;a.vy-=normal[1]*impulse*wa;a.vz-=normal[2]*impulse*wa;
      b.vx+=normal[0]*impulse*wb;b.vy+=normal[1]*impulse*wb;b.vz+=normal[2]*impulse*wb;
    }
    // Sleep follows the actual motion of the supported pile.
    this.quietTime=!this.held&&this.bodies.every(b=>b.visible)&&motion<.001?this.quietTime+dt:0;
    if(this.quietTime>.6){
      // Remove the final contact error without adding gravity before sleeping.
      this.projectContacts(600,.00001);
      for(const b of this.bodies)this.contain(b);
      this.sleeping=true;this.active=false;
      for(const b of this.bodies)b.vx=b.vy=b.vz=0;
    }
    this.updateContacts();
  }
  projectContacts(iterations,tolerance=0){
    // Position constraints are also used by the one-time settling pass.
    for(let iteration=0;iteration<iterations;iteration++){
      let overlap=0;
      for(const b of this.bodies)this.contain(b);
      for(let i=0;i<this.bodies.length;i++)for(let j=i+1;j<this.bodies.length;j++){
        const a=this.bodies[i],b=this.bodies[j],dx=b.x-a.x,dy=b.y-a.y,dz=b.z-a.z;
        if(!a.visible||!b.visible)continue;
        const squared=dx*dx+dy*dy+dz*dz;
        if(squared>=(broadRadius(a)+broadRadius(b)+.003)**2)continue;
        const distance=Math.sqrt(squared),nx=distance?dx/distance:1,ny=distance?dy/distance:0,nz=distance?dz/distance:0;
        const radii=directionSupport(a,[nx,ny,nz])+directionSupport(b,[-nx,-ny,-nz])+.003;
        if(distance>=radii)continue;
        if(iteration===0&&this.impactPairs){
          const av=a.incoming??[0,0,0],bv=b.incoming??[0,0,0],closing=(bv[0]-av[0])*nx+(bv[1]-av[1])*ny+(bv[2]-av[2])*nz;
          if(closing<-.65)this.impactPairs.push({a,b,normal:[nx,ny,nz],closing});
        }
        const wa=a===this.held?0:1,wb=b===this.held?0:1,total=wa+wb,correction=(radii-distance)/total;
        overlap=Math.max(overlap,radii-distance);
        a.x-=nx*correction*wa;a.y-=ny*correction*wa;a.z-=nz*correction*wa;
        b.x+=nx*correction*wb;b.y+=ny*correction*wb;b.z+=nz*correction*wb;
      }
      if(tolerance&&overlap<tolerance)break;
    }
  }
  contain(b){
    if(!b.visible)return;
    const floor=floorSupport(b);
    if(b.y<floor){
      if(b!==this.held)b.floorImpact=Math.max(b.floorImpact??0,-(b.incoming?.[1]??0));
      b.y=floor;b.vy=Math.max(0,b.vy);
    }
    const x=clamp(b.x,-this.halfWidth+directionSupport(b,[-1,0,0]),this.halfWidth-directionSupport(b,[1,0,0])),z=clamp(b.z,-this.halfDepth+directionSupport(b,[0,0,-1]),this.halfDepth-directionSupport(b,[0,0,1]));
    if(x!==b.x){b.x=x;b.vx=0;}
    if(z!==b.z){b.z=z;b.vz=0;}
  }
  updateContacts(){
    const visible=this.bodies.filter(b=>b.visible),pairs=[];
    for(const b of this.bodies){b.contacts=[];b.weight=1;}
    for(let i=0;i<visible.length;i++)for(let j=i+1;j<visible.length;j++){
      const a=visible[i],b=visible[j],delta=[b.x-a.x,b.y-a.y,b.z-a.z],distance=Math.hypot(...delta);
      if(distance>broadRadius(a)+broadRadius(b)+.025)continue;
      const normal=distance?delta.map(n=>n/distance):[1,0,0];
      if(distance>directionSupport(a,normal)+directionSupport(b,normal.map(n=>-n))+.025)continue;
      const gap=distance-directionSupport(a,normal)-directionSupport(b,normal.map(n=>-n));
      const proximity=clamp((.025-gap)/.022,0,1);
      pairs.push({a,b,normal,distance,proximity:proximity*proximity*(3-2*proximity)});
    }
    // Carry the weight of upper toys through their supporting contacts.
    for(const upper of [...visible].sort((a,b)=>b.y-a.y)){
      const floorWeight=clamp((.025-(upper.y-floorSupport(upper)))/.025,0,1);
      const supports=pairs.flatMap(p=>p.a===upper&&p.normal[1]<0?[{body:p.b,weight:p.normal[1]**2*p.proximity}]:p.b===upper&&p.normal[1]>0?[{body:p.a,weight:p.normal[1]**2*p.proximity}]:[]);
      const total=supports.reduce((sum,s)=>sum+s.weight,0);
      // Shallow contacts carry a continuous share instead of switching half
      // the weight at an angle threshold and exciting the compliant cages.
      for(const support of supports)support.body.weight+=upper.weight*support.weight/Math.max(.08,total+floorWeight);
    }
    for(const b of visible)if(b.y-floorSupport(b)<.025){
      b.contacts.push({otherId:null,normal:[0,1,0],point:supportPoint(b,[0,-1,0]),restPoint:supportPoint(b,[0,-1,0],true),depth:Math.max(0,floorSupport(b)-b.y),load:Math.min(1.5,b.weight*.22+Math.max(0,-(b.incoming?.[1]??0))*.08)});
    }
    for(const {a,b,normal,distance,proximity} of pairs){
      const av=a.incoming??[0,0,0],bv=b.incoming??[0,0,0];
      const impact=Math.max(0,-normal.reduce((sum,n,i)=>sum+n*(bv[i]-av[i]),0));
      const upper=normal[1]>0?b:a;
      const load=proximity*Math.min(1.5,.1+.22*upper.weight*Math.abs(normal[1])+impact*.07+(a===this.held||b===this.held?.2:0));
      const reverse=normal.map(n=>-n),depth=Math.max(0,directionSupport(a,normal)+directionSupport(b,normal.map(n=>-n))-distance)*.5;
      a.contacts.push({otherId:b.id,normal:reverse,point:supportPoint(a,normal),restPoint:supportPoint(a,normal,true),load,depth});
      b.contacts.push({otherId:a.id,normal:[...normal],point:supportPoint(b,reverse),restPoint:supportPoint(b,reverse,true),load,depth});
    }
  }
}
